import { useState } from "react";
import {
  Headphones,
  PencilRuler,
  Code2,
  Rocket,
  RefreshCw,
} from "lucide-react";

const steps = [
  {
    num: "01",
    title: "Découverte",
    icon: Headphones,
    desc: "Un premier appel pour comprendre votre activité, votre clientèle, vos objectifs. Aucun engagement, juste une vraie conversation pour voir si l'on s'entend.",
    duration: "30 min · offert",
    meta: [
      { label: "Durée", value: "30 min" },
      { label: "Coût", value: "Offert" },
      { label: "Format", value: "Visio" },
    ],
  },
  {
    num: "02",
    title: "Conception",
    icon: PencilRuler,
    desc: "On définit ensemble la structure de votre site, ses contenus et son ton de marque. Tout est posé sur le papier avant la première ligne de code. Vous validez chaque choix.",
    duration: "3 à 5 jours",
    meta: [
      { label: "Durée", value: "3 à 5 jours" },
      { label: "Livrable", value: "Structure & Vision" },
      { label: "Validation", value: "Vous" },
    ],
  },
  {
    num: "03",
    title: "Développement",
    icon: Code2,
    desc: "Maquettes, prototype, puis création de votre site sur mesure. Vous suivez l'avancée en direct et on ajuste ensemble à chaque étape.",
    duration: "2 à 4 semaines",
    meta: [
      { label: "Durée", value: "2 à 4 sem." },
      { label: "Tests", value: "Multi-supports" },
      { label: "Suivi", value: "Hebdo" },
    ],
  },
  {
    num: "04",
    title: "Mise en ligne",
    icon: Rocket,
    desc: "Votre site est en ligne et pleinement opérationnel. Hébergement, domaine, emails pro et sécurité : tout est configuré pour accueillir vos premiers visiteurs. Une formation d'1 h vous remet les clés en main.",
    duration: "1 journée",
    meta: [
      { label: "Durée", value: "1 journée" },
      { label: "Inclus", value: "Sécurité & emails pro" },
      { label: "Formation", value: "1 h offerte" },
    ],
  },
  {
    num: "05",
    title: "Suivi continu",
    icon: RefreshCw,
    desc: "Sauvegardes, mises à jour, évolutions et support : votre site continue de bien fonctionner mois après mois. Vous restez serein, je m'occupe de toute la mécanique.",
    duration: "En continu",
    meta: [
      { label: "Sauvegardes", value: "Quotidiennes" },
      { label: "Support", value: "24 h ouvrées" },
      { label: "Évolutions", value: "Incluses" },
    ],
  },
];

export default function Process() {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = steps[activeIdx];

  return (
    <section
      id="methode"
      data-testid="process-section"
      className="py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        {/* Heading */}
        <div className="grid grid-cols-12 gap-6 mb-12 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <span className="eyebrow">— Méthode</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium text-[#0B1027] mt-5 leading-[1.05]">
              De votre idée à votre site,
              <br />
              <em className="not-italic italic font-light text-aurora">
                cinq étapes maîtrisées.
              </em>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="text-[#0F172A]/75 leading-relaxed text-base md:text-lg">
              À chaque étape, vous savez ce qui est livré, quand, et pourquoi.
              Pas de zone d'ombre, pas de mauvaise surprise.
            </p>
          </div>
        </div>

        {/* Side-by-side layout — compact number rail on mobile, full tabs on desktop */}
        <div className="grid grid-cols-12 gap-3 sm:gap-4 md:gap-10 items-start">
          {/* Mobile: compact number rail (visible < lg) */}
          <div
            data-testid="process-tabs-mobile"
            className="col-span-2 lg:hidden flex flex-col gap-2 sticky top-24"
            role="tablist"
            aria-label="Étapes du processus"
          >
            {steps.map((s, i) => {
              const isActive = i === activeIdx;
              return (
                <button
                  key={s.num}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveIdx(i)}
                  data-testid={`process-tab-mobile-${s.num}`}
                  className={`group rounded-xl aspect-square flex items-center justify-center shadow-tactile transition-all duration-300 ${
                    isActive
                      ? "btn-aurora text-white shadow-tactile-lg ring-aurora scale-105"
                      : "bg-white text-[#0B1027] hover:shadow-tactile-lg active:scale-95"
                  }`}
                  aria-label={`Étape ${s.num} — ${s.title}`}
                >
                  <span
                    className={`font-display text-2xl sm:text-3xl font-light ${
                      isActive ? "text-white" : "text-aurora"
                    }`}
                  >
                    {s.num}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Desktop: full tab cards (visible lg+) */}
          <div
            data-testid="process-tabs"
            className="hidden lg:block lg:col-span-5 space-y-3"
            role="tablist"
            aria-label="Étapes du processus"
          >
            {steps.map((s, i) => {
              const isActive = i === activeIdx;
              return (
                <button
                  key={s.num}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveIdx(i)}
                  data-testid={`process-tab-${s.num}`}
                  className={`group w-full text-left rounded-2xl p-5 md:p-6 shadow-tactile transition-all duration-300 hover-lift ${
                    isActive
                      ? "btn-aurora text-white shadow-tactile-lg ring-aurora"
                      : "bg-white text-[#0B1027] hover:shadow-tactile-lg"
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <span
                      className={`font-display text-3xl md:text-4xl font-light ${
                        isActive ? "text-white" : "text-aurora"
                      }`}
                    >
                      {s.num}
                    </span>
                    <span
                      className={`text-[10px] uppercase tracking-[0.18em] font-bold shrink-0 ${
                        isActive ? "text-white/85" : "text-[#0B1027]/60"
                      }`}
                    >
                      {s.duration}
                    </span>
                  </div>
                  <div
                    className={`font-display text-xl md:text-2xl tracking-tight mt-1 ${
                      isActive ? "text-white" : "text-[#0B1027]"
                    }`}
                  >
                    {s.title}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Active panel */}
          <div className="col-span-10 lg:col-span-7 lg:sticky lg:top-28">
            <div
              data-testid="process-active-panel"
              className="relative overflow-hidden rounded-3xl bg-[#0B1027] text-white p-5 sm:p-7 md:p-12 shadow-tactile-lg min-h-[420px] md:min-h-[520px]"
            >
              {/* Aurora glow */}
              <div
                aria-hidden
                className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-50 blur-2xl"
                style={{
                  background:
                    "radial-gradient(circle, rgba(168,85,247,0.55), transparent 70%)",
                }}
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-32 -left-20 w-80 h-80 rounded-full opacity-40 blur-3xl"
                style={{
                  background:
                    "radial-gradient(circle, rgba(236,72,153,0.45), transparent 70%)",
                }}
              />

              <div key={active.num} className="relative animate-fade-in">
                {/* Icon — top right, aligned with the big number */}
                {active.icon && (
                  <div
                    aria-hidden
                    data-testid="process-step-icon"
                    className="hidden md:flex absolute top-24 lg:top-28 right-28 lg:right-40 w-[110px] h-[110px] lg:w-[130px] lg:h-[130px] items-center justify-center pointer-events-none"
                  >
                    <div
                      className="absolute inset-[-18px] rounded-full blur-2xl opacity-80"
                      style={{
                        background:
                          "radial-gradient(circle, rgba(168,85,247,0.55) 0%, rgba(236,72,153,0.3) 45%, transparent 75%)",
                      }}
                    />
                    <div
                      className="relative w-full h-full rounded-3xl flex items-center justify-center backdrop-blur-md border border-white/20"
                      style={{ background: "rgba(255,255,255,0.06)" }}
                    >
                      <active.icon
                        size={48}
                        strokeWidth={1.4}
                        className="text-white lg:w-[56px] lg:h-[56px]"
                      />
                    </div>
                  </div>
                )}

                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/70">
                  — Étape en cours
                </span>
                <div
                  className="font-display font-light leading-none mt-3 bg-clip-text text-transparent"
                  style={{
                    fontSize: "clamp(96px, 14vw, 180px)",
                    backgroundImage:
                      "linear-gradient(135deg, #818CF8 0%, #C084FC 50%, #F9A8D4 100%)",
                  }}
                >
                  {active.num}
                </div>
                <h3 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-2">
                  {active.title}
                </h3>
                <p className="mt-5 text-white/80 leading-relaxed text-base md:text-lg max-w-xl">
                  {active.desc}
                </p>

                <div className="mt-8 md:mt-10 pt-6 md:pt-8 border-t border-white/15 grid grid-cols-3 gap-4 md:gap-6">
                  {active.meta.map((m) => (
                    <div key={m.label}>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold">
                        {m.label}
                      </div>
                      <div className="font-display text-base md:text-xl mt-1 text-white">
                        {m.value}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Progress */}
                <div className="mt-10 flex items-center gap-4">
                  <div className="flex-1 h-[3px] rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full transition-all duration-700 rounded-full"
                      style={{
                        width: `${((activeIdx + 1) / steps.length) * 100}%`,
                        background:
                          "linear-gradient(90deg, #6366F1 0%, #A855F7 50%, #EC4899 100%)",
                      }}
                    />
                  </div>
                  <span className="text-xs text-white/60 tabular-nums">
                    {activeIdx + 1} / {steps.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
